import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Loader2,
  Palette,
  Type,
  Link2,
  RotateCcw,
  Mail,
  FileText,
  Images,
  PanelBottom,
  PanelTop,
  Hand,
  AlignLeft,
  Heart,
  PenLine,
} from 'lucide-react';
import {
  CX_EMAIL_FONT_OPTIONS,
  CX_EMAIL_HEADER_ALIGN_OPTIONS,
  CX_EMAIL_HEADER_SIZE_OPTIONS,
  resolveEmpresaHeaderVisual,
  CX_EMAIL_PLACEHOLDERS,
  CX_EMAIL_PREVIEW_SAMPLE,
  DEFAULT_CX_EMAIL_TEMPLATE,
  mergeCxEmailTemplate,
} from '../utils/cxEmailTemplateDefaults';
import { renderCxEmailTemplateHtml } from '../utils/cxEmailTemplateRender';
import CxEmailCompanyImages from './CxEmailCompanyImages';
import CxEmailOptionPicker from './CxEmailOptionPicker';
import CxEmailHeaderScaleSlider from './CxEmailHeaderScaleSlider';
import '../../board/board-search-filters/BoardSearchFilters.css';
import './CustomerExperienceView.css';
import './CxEmailTemplateModal.css';

function templateSnapshot(template) {
  return JSON.stringify(mergeCxEmailTemplate(template));
}

const EMAIL_MODAL_TABS = [
  { id: 'visual', label: 'Visual', sectionTitle: 'Visual', Icon: Palette },
  { id: 'images', label: 'Imagens', sectionTitle: 'Imagens', Icon: Images },
  { id: 'content', label: 'Conteúdo', sectionTitle: 'Conteúdo', Icon: FileText },
];

function CxEmailPlaceholderChips({ onInsert }) {
  return (
    <div className="cx-email-placeholders">
      {CX_EMAIL_PLACEHOLDERS.map((p) => (
        <button key={p.key} type="button" onClick={() => onInsert(p.key)}>
          {`{{${p.key}}}`}
        </button>
      ))}
    </div>
  );
}

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

function ColorField({ label, value, onChange }) {
  const safeValue = /^#[0-9A-Fa-f]{6}$/.test(value) ? value : '#000000';
  return (
    <label className="cx-email-field cx-email-field--color">
      <span className="cx-email-field-label">{label}</span>
      <span className="cx-email-color-picker">
        <input
          type="text"
          className="cx-email-color-hex"
          value={value}
          maxLength={7}
          spellCheck={false}
          aria-label={`${label} — código hexadecimal`}
          onChange={(e) => {
            const v = e.target.value;
            if (/^#[0-9A-Fa-f]{0,6}$/.test(v)) onChange(v);
          }}
        />
        <span className="cx-email-color-swatch" style={{ backgroundColor: safeValue }} aria-hidden="true">
          <input
            type="color"
            className="cx-email-color-native"
            value={safeValue}
            onChange={(e) => onChange(e.target.value)}
            aria-label={`Selecionar ${label}`}
          />
        </span>
      </span>
    </label>
  );
}

export default function CxEmailTemplateModal({ open, initialTemplate, onClose, onSaved }) {
  const [tab, setTab] = useState('visual');
  const [draft, setDraft] = useState(() => mergeCxEmailTemplate(initialTemplate));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [companyImages, setCompanyImages] = useState([]);
  const [baselineJson, setBaselineJson] = useState('');
  const [imagesReloadKey, setImagesReloadKey] = useState(0);
  const bodyRef = useRef(null);
  const footerLegalRef = useRef(null);
  const footerSignatureRef = useRef(null);
  const activeFooterFieldRef = useRef('legal');

  useEffect(() => {
    if (open) {
      setDraft(mergeCxEmailTemplate(initialTemplate));
      setBaselineJson(templateSnapshot(initialTemplate));
      setTab('visual');
      setError('');
      setCompanyImages([]);
      setImagesReloadKey((k) => k + 1);
    }
  }, [open, initialTemplate]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/vc-usr/cx/config/images/empresas', { headers: authHeaders() });
        const json = await res.json().catch(() => ({}));
        if (!res.ok || json.success === false || cancelled) return;
        const list = json.data?.companies || [];
        const firstWithLogo = list.find((c) => c.image?.hasLogo || c.image?.viewUrl) || null;
        if (!cancelled) {
          setCompanyImages(firstWithLogo ? [firstWithLogo, ...list.filter((c) => c !== firstWithLogo)] : list);
        }
      } catch {
        /* pré-visualização usa fallback de exemplo */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, imagesReloadKey]);

  const isDirty = useMemo(
    () => templateSnapshot(draft) !== baselineJson,
    [draft, baselineJson]
  );

  const previewSampleData = useMemo(() => {
    const previewCompany = companyImages[0];
    return {
      ...CX_EMAIL_PREVIEW_SAMPLE,
      empresa: String(previewCompany?.nome || '').trim() || CX_EMAIL_PREVIEW_SAMPLE.empresa,
    };
  }, [companyImages]);

  const previewHtml = useMemo(() => {
    let visual = draft.visual;
    const previewCompany = companyImages[0];
    if (previewCompany) {
      visual = resolveEmpresaHeaderVisual(
        visual,
        previewCompany.codigo,
        previewCompany.nome,
        previewCompany.image?.viewUrl || ''
      );
    }
    return renderCxEmailTemplateHtml({ ...draft, visual }, previewSampleData);
  }, [draft, companyImages, previewSampleData]);

  const patchVisual = useCallback((patch) => {
    setDraft((prev) => ({ ...prev, visual: { ...prev.visual, ...patch } }));
  }, []);

  const patchContent = useCallback((patch) => {
    setDraft((prev) => ({ ...prev, content: { ...prev.content, ...patch } }));
  }, []);

  const insertTokenAtField = useCallback((key, currentValue, onUpdate, inputRef) => {
    const token = `{{${key}}}`;
    const el = inputRef?.current;
    if (el && typeof el.selectionStart === 'number') {
      const start = el.selectionStart ?? currentValue.length;
      const end = el.selectionEnd ?? start;
      const next = `${currentValue.slice(0, start)}${token}${currentValue.slice(end)}`;
      onUpdate(next);
      requestAnimationFrame(() => {
        el.focus();
        const pos = start + token.length;
        el.setSelectionRange(pos, pos);
      });
      return;
    }
    onUpdate(`${currentValue}${currentValue ? ' ' : ''}${token}`);
  }, []);

  const insertBodyPlaceholder = useCallback(
    (key) => {
      insertTokenAtField(key, draft.content.body, (body) => patchContent({ body }), bodyRef);
    },
    [draft.content.body, insertTokenAtField, patchContent]
  );

  const insertFooterPlaceholder = useCallback(
    (key) => {
      if (activeFooterFieldRef.current === 'signature') {
        insertTokenAtField(
          key,
          draft.visual.footerSignature,
          (footerSignature) => patchVisual({ footerSignature }),
          footerSignatureRef
        );
        return;
      }
      insertTokenAtField(
        key,
        draft.visual.footerLegal,
        (footerLegal) => patchVisual({ footerLegal }),
        footerLegalRef
      );
    },
    [draft.visual.footerLegal, draft.visual.footerSignature, insertTokenAtField, patchVisual]
  );

  const resetDefaults = () => {
    setDraft(mergeCxEmailTemplate(DEFAULT_CX_EMAIL_TEMPLATE));
  };

  const handleCancel = () => {
    if (saving || !isDirty) return;
    onClose?.();
  };

  const handleSave = async () => {
    if (!isDirty || saving) return;
    setSaving(true);
    setError('');
    try {
      const payload = mergeCxEmailTemplate({
        ...draft,
        visual: { ...draft.visual, heroImageUrl: '', heroImageEnabled: false },
      });
      const res = await fetch('/api/vc-usr/cx/config/email-template', {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ emailTemplate: payload }),
      });
      const json = await res.json().catch(() => ({}));
      if (!res.ok || json.success === false) throw new Error(json.message || `Erro ${res.status}`);
      onSaved?.(json.data?.emailTemplate || draft);
      onClose?.();
    } catch (e) {
      setError(e.message || 'Erro ao salvar template');
    } finally {
      setSaving(false);
    }
  };

  if (!open) return null;

  const activeTabMeta = EMAIL_MODAL_TABS.find((item) => item.id === tab) || EMAIL_MODAL_TABS[0];

  return createPortal(
    <div
      className="modal-overlay cx-email-modal-overlay"
      role="presentation"
      onClick={() => {
        if (saving) return;
        if (isDirty) handleCancel();
        else onClose?.();
      }}
    >
      <div
        className="cx-email-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cx-email-modal-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="cx-email-modal-body">
          <aside className="cx-email-sidebar">
            <div className="cx-email-sidebar-header">
              <h2 id="cx-email-modal-title" className="cx-email-sidebar-title">
                Modelo de email
              </h2>
              <p className="cx-email-sidebar-subtitle">
                Personalize visual e conteúdo do e-mail enviado ao cliente.
              </p>
            </div>
            <nav className="cx-email-nav" aria-label="Seções do modelo de e-mail">
              {EMAIL_MODAL_TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  className={`cx-email-nav-item${tab === item.id ? ' active' : ''}`}
                  onClick={() => setTab(item.id)}
                >
                  <span className="cx-email-nav-icon">
                    <item.Icon size={14} strokeWidth={2} aria-hidden="true" />
                  </span>
                  {item.label}
                </button>
              ))}
            </nav>
          </aside>

          <div className="cx-email-main">
            <div className="cx-email-main-header">
              <h3 className="cx-email-main-title">{activeTabMeta.sectionTitle}</h3>
              <button
                type="button"
                className="cx-email-main-close"
                aria-label="Fechar"
                disabled={saving}
                onClick={onClose}
                title="Fechar"
              >
                ✕
              </button>
            </div>

            <div className="cx-email-main-workspace">
              <div className="cx-email-editor">
                {tab === 'visual' ? (
                  <div className="cx-email-editor-scroll">
                <div className="cx-email-config-rows">
                <div className="cx-email-config-row">
                  <div className="cx-email-config-row-head">
                    <Palette size={14} aria-hidden />
                    <h3>Cores do template</h3>
                  </div>
                  <div className="cx-email-color-grid">
                    <div className="cx-email-color-row cx-email-color-row--pair">
                      <ColorField label="Primária" value={draft.visual.primaryColor} onChange={(v) => patchVisual({ primaryColor: v })} />
                      <ColorField label="Secundária" value={draft.visual.secondaryColor} onChange={(v) => patchVisual({ secondaryColor: v })} />
                    </div>
                    <div className="cx-email-color-row cx-email-color-row--triple">
                      <ColorField label="Fundo" value={draft.visual.backgroundColor} onChange={(v) => patchVisual({ backgroundColor: v })} />
                      <span className="cx-email-color-pipe" aria-hidden="true">|</span>
                      <ColorField label="Botão" value={draft.visual.buttonColor} onChange={(v) => patchVisual({ buttonColor: v })} />
                      <ColorField label="Texto do botão" value={draft.visual.buttonTextColor} onChange={(v) => patchVisual({ buttonTextColor: v })} />
                    </div>
                  </div>
                </div>

                <div className="cx-email-config-row">
                  <div className="cx-email-config-row-head">
                    <PanelTop size={14} aria-hidden />
                    <h3>Configurações do header</h3>
                  </div>
                  <label className="cx-email-field">
                    <span className="cx-email-field-label">Área da logomarca</span>
                    <CxEmailHeaderScaleSlider
                      value={draft.visual.headerLogoScale}
                      onChange={(v) => patchVisual({ headerLogoScale: v })}
                    />
                    <span className="cx-email-field-hint">
                      Reduz apenas o espaço vertical do cabeçalho colorido. A logomarca mantém o tamanho original.
                    </span>
                  </label>
                  <div className="cx-email-field cx-email-field--compact">
                    <span className="cx-email-field-label">Alinhamento</span>
                    <div className="cx-email-segmented cx-email-segmented--inline">
                      {CX_EMAIL_HEADER_ALIGN_OPTIONS.map((opt) => (
                        <button
                          key={opt.value}
                          type="button"
                          className={draft.visual.headerAlign === opt.value ? 'is-active' : ''}
                          onClick={() => patchVisual({ headerAlign: opt.value })}
                        >
                          {opt.label}
                        </button>
                      ))}
                    </div>
                  </div>
                  <div className="cx-email-field cx-email-field--compact">
                    <CxEmailOptionPicker
                      label="Espaçamento"
                      value={draft.visual.headerSize}
                      onChange={(v) => patchVisual({ headerSize: v })}
                      options={CX_EMAIL_HEADER_SIZE_OPTIONS}
                      ariaLabel="Espaçamento do header"
                    />
                  </div>
                </div>

                <div className="cx-email-config-row">
                  <div className="cx-email-config-row-head">
                    <Type size={14} aria-hidden />
                    <h3>Tipografia</h3>
                  </div>
                  <CxEmailOptionPicker
                    label="Fonte"
                    value={draft.visual.fontFamily}
                    onChange={(v) => patchVisual({ fontFamily: v })}
                    options={CX_EMAIL_FONT_OPTIONS}
                    ariaLabel="Fonte do e-mail"
                  />
                </div>

                <div className="cx-email-config-row cx-email-config-row--last">
                  <div className="cx-email-config-row-head">
                    <PanelBottom size={14} aria-hidden />
                    <h3>Layout do rodapé</h3>
                  </div>
                  <CxEmailPlaceholderChips onInsert={insertFooterPlaceholder} />
                  <p className="cx-email-field-hint">Parâmetros inseridos no campo em foco (informações legais ou assinatura).</p>
                  <label className="cx-email-field">
                    <span className="cx-email-field-label">Informações legais</span>
                    <textarea
                      ref={footerLegalRef}
                      className="cx-email-textarea"
                      rows={2}
                      value={draft.visual.footerLegal}
                      onFocus={() => {
                        activeFooterFieldRef.current = 'legal';
                      }}
                      onChange={(e) => patchVisual({ footerLegal: e.target.value })}
                    />
                  </label>
                  <label className="cx-email-field">
                    <span className="cx-email-field-label">Links</span>
                    <input
                      className="cx-email-input"
                      value={draft.visual.footerLinks}
                      placeholder="Site | Política de privacidade"
                      onChange={(e) => patchVisual({ footerLinks: e.target.value })}
                    />
                  </label>
                  <label className="cx-email-field">
                    <span className="cx-email-field-label">Assinatura do rodapé</span>
                    <input
                      ref={footerSignatureRef}
                      className="cx-email-input"
                      value={draft.visual.footerSignature}
                      onFocus={() => {
                        activeFooterFieldRef.current = 'signature';
                      }}
                      onChange={(e) => patchVisual({ footerSignature: e.target.value })}
                    />
                  </label>
                </div>
                </div>
              </div>
            ) : tab === 'images' ? (
              <div className="cx-email-editor-scroll">
                <div className="cx-email-config-rows">
                  <CxEmailCompanyImages
                    active={tab === 'images'}
                    reloadKey={imagesReloadKey}
                    onCompaniesChange={setCompanyImages}
                  />
                </div>
              </div>
            ) : (
              <div className="cx-email-editor-scroll">
                <div className="cx-email-config-rows">
                <div className="cx-email-config-row">
                  <div className="cx-email-config-row-head">
                    <Mail size={14} aria-hidden />
                    <h3>Assunto do e-mail</h3>
                  </div>
                  <input
                    className="cx-email-input"
                    value={draft.content.subject}
                    onChange={(e) => patchContent({ subject: e.target.value })}
                  />
                </div>

                <div className="cx-email-config-row">
                  <div className="cx-email-config-row-head">
                    <Hand size={14} aria-hidden />
                    <h3>Saudação</h3>
                  </div>
                  <input
                    className="cx-email-input"
                    value={draft.content.greeting}
                    onChange={(e) => patchContent({ greeting: e.target.value })}
                  />
                </div>

                <div className="cx-email-config-row">
                  <div className="cx-email-config-row-head">
                    <AlignLeft size={14} aria-hidden />
                    <h3>Corpo do e-mail</h3>
                  </div>
                  <CxEmailPlaceholderChips onInsert={insertBodyPlaceholder} />
                  <textarea
                    ref={bodyRef}
                    className="cx-email-textarea cx-email-textarea--body"
                    rows={6}
                    value={draft.content.body}
                    onChange={(e) => patchContent({ body: e.target.value })}
                  />
                </div>

                <div className="cx-email-config-row">
                  <div className="cx-email-config-row-head">
                    <Link2 size={14} aria-hidden />
                    <h3>Texto do botão de avaliação</h3>
                  </div>
                  <input
                    className="cx-email-input"
                    value={draft.content.buttonText}
                    onChange={(e) => patchContent({ buttonText: e.target.value })}
                  />
                </div>

                <div className="cx-email-config-row">
                  <div className="cx-email-config-row-head">
                    <Heart size={14} aria-hidden />
                    <h3>Mensagem de agradecimento</h3>
                  </div>
                  <textarea
                    className="cx-email-textarea"
                    rows={2}
                    value={draft.content.thankYou}
                    onChange={(e) => patchContent({ thankYou: e.target.value })}
                  />
                </div>

                <div className="cx-email-config-row cx-email-config-row--last">
                  <div className="cx-email-config-row-head">
                    <PenLine size={14} aria-hidden />
                    <h3>Assinatura personalizada</h3>
                  </div>
                  <input
                    className="cx-email-input"
                    value={draft.content.signature}
                    onChange={(e) => patchContent({ signature: e.target.value })}
                  />
                </div>
                </div>
              </div>
            )}
          </div>

          <aside className="cx-email-preview">
            <div className="cx-email-preview-head">
              <span>Pré-visualização</span>
              <span className="cx-email-preview-sub">Dados de exemplo</span>
            </div>
            <iframe
              title="Pré-visualização do e-mail"
              className="cx-email-preview-frame"
              srcDoc={previewHtml}
              sandbox="allow-same-origin"
            />
          </aside>
            </div>

            {error ? <p className="cx-email-modal-error">{error}</p> : null}

            <footer className="cx-email-modal-footer">
              <button type="button" className="cx-email-btn cx-email-btn--ghost cx-email-btn--link" disabled={saving} onClick={resetDefaults}>
                <RotateCcw size={14} aria-hidden />
                Restaurar padrão
              </button>
              <div className="cx-email-modal-footer-actions">
                <button
                  type="button"
                  className="cx-email-btn cx-email-btn--ghost cx-email-btn--link"
                  disabled={saving || !isDirty}
                  onClick={handleCancel}
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  className="cx-email-btn cx-email-btn--primary"
                  disabled={saving || !isDirty}
                  onClick={handleSave}
                >
                  {saving ? <Loader2 size={14} className="cx-exp-spin" aria-hidden /> : null}
                  Salvar modelo
                </button>
              </div>
            </footer>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
}
